import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import NumberLineInterval from "../../../../components/visuals/NumberLineInterval";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const coeffX = (k) => {
  if (k === 1) return "x";
  if (k === -1) return "-x";
  return `${k}x`;
};

const coeffParen = (k) => {
  if (k === 1) return "";
  if (k === -1) return "-";
  return `${k}`;
};

const signed = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);

const relLatexMap = {
  "<": "<",
  "<=": "\\le",
  ">": ">",
  ">=": "\\ge",
};

function flipRel(rel) {
  if (rel === "<") return ">";
  if (rel === "<=") return ">=";
  if (rel === ">") return "<";
  return "<=";
}

function intervalLatex(boundary, dir, closed) {
  const b = `${boundary}`;
  if (dir === "left") return `(-\\infty,\\; ${b}${closed ? "]" : ")"}`;
  return `${closed ? "[" : "("}${b},\\; +\\infty)`;
}

function optionKey(o) {
  return `${o.boundary}|${o.dir}|${o.closed ? 1 : 0}`;
}

function linearLatex(coef, constant) {
  const xPart = coeffX(coef);
  if (coef === 0) return `${constant}`;
  if (constant === 0) return xPart;
  return `${xPart} ${signed(constant)}`;
}

function generateMay2025OriginalTask() {
  const options = [
    { boundary: 3, dir: "left", closed: true, isCorrect: false },
    { boundary: 3, dir: "right", closed: true, isCorrect: false },
    { boundary: -9, dir: "left", closed: true, isCorrect: false },
    { boundary: -9, dir: "right", closed: true, isCorrect: true },
  ].map((option, index) => ({
    ...option,
    label: LETTERS[index],
    latex: intervalLatex(option.boundary, option.dir, option.closed),
  }));

  return {
    type: "MaturaLinearInequalityGraphClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 6,
    },
    data: {
      variant: "minusParen",
      m: 2,
      n: 1,
      s: 2,
      p: 3,
      lhsLatex: "3 - 2(1 - 2x)",
      rhsLatex: "2x - 17",
      rhsStyle: "xPlusConst",
      inequalityLatex: "3 - 2(1 - 2x) \\ge 2x - 17",
      rel: ">=",
      finalRel: ">=",
      lhsCoeff: 4,
      lhsConst: 1,
      rhsCoeff: 2,
      rhsConst: -17,
      K: 2,
      z: -9,
      closed: true,
      dir: "right",
      options,
      correctIndex: 3,
      maxPoints: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  while (true) {
    const variant = pick(["minusParen", "plusParen"]);

    const m = randInt(1, 4);
    const n = randInt(1, 9);
    const s = randInt(1, 4);

    const lhsConstTarget = randInt(-14, 18);
    const lhsCoeff = variant === "minusParen" ? m * s : -m * s;

    const p =
      variant === "minusParen"
        ? lhsConstTarget + m * n
        : lhsConstTarget - m * n;

    if (Math.abs(p) > 24) continue;

    const K = pick([-8, -7, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6, 7, 8]);
    const z = randInt(-9, 9);
    const rhsCoeff = lhsCoeff - K;
    const rhsConst = lhsConstTarget + K * z;

    // Każda zapisana strona ma faktycznie zawierać niewiadomą. Sam warunek
    // K != 0 chroni rozwiązanie przed degeneracją, ale wcześniej dopuszczał
    // nieczytelne warianty w rodzaju „0x + 32”.
    if (rhsCoeff === 0 || Math.abs(rhsCoeff) > 10 || Math.abs(rhsConst) > 40) continue;

    const rhsStyle =
      rhsCoeff < 0 && Math.random() < 0.5 ? "constMinusX" : "xPlusConst";

    const rel = pick(["<", "<=", ">", ">="]);
    const finalRel = K > 0 ? rel : flipRel(rel);
    const closed = finalRel === "<=" || finalRel === ">=";
    const dir = finalRel === "<" || finalRel === "<=" ? "left" : "right";

    const parenPart = `${n} - ${coeffX(s)}`;
    const lhsLatex =
      variant === "minusParen"
        ? `${p} - ${coeffParen(m)}(${parenPart})`
        : `${p} + ${coeffParen(m)}(${parenPart})`;

    const rhsLatex =
      rhsStyle === "constMinusX"
        ? `${rhsConst} - ${coeffX(Math.abs(rhsCoeff))}`
        : linearLatex(rhsCoeff, rhsConst);

    const inequalityLatex = `${lhsLatex} \\; ${relLatexMap[rel]} \\; ${rhsLatex}`;

    const candidateAlt = [
      -z,
      z - 2,
      z + 2,
      z - 1,
      z + 1,
      z - 3,
      z + 3,
    ].filter((v) => Number.isFinite(v) && v !== z && Math.abs(v) <= 12);
    if (!candidateAlt.length) continue;
    const altBoundary = pick(candidateAlt);

    const rawOptions = [
      { boundary: z, dir, closed, isCorrect: true },
      { boundary: z, dir: dir === "left" ? "right" : "left", closed, isCorrect: false },
      { boundary: altBoundary, dir, closed, isCorrect: false },
      {
        boundary: altBoundary,
        dir: dir === "left" ? "right" : "left",
        closed,
        isCorrect: false,
      },
    ];

    const unique = [];
    const seen = new Set();
    for (const opt of rawOptions) {
      const k = optionKey(opt);
      if (!seen.has(k)) {
        seen.add(k);
        unique.push(opt);
      }
    }

    while (unique.length < 4) {
      const extraBoundary = z + pick([-6, -5, -4, -3, -2, 2, 3, 4, 5, 6]);
      const extraDir = Math.random() < 0.5 ? "left" : "right";
      const extra = { boundary: extraBoundary, dir: extraDir, closed, isCorrect: false };
      const k = optionKey(extra);
      if (!seen.has(k)) {
        seen.add(k);
        unique.push(extra);
      }
    }

    const options = unique
      .sort(() => Math.random() - 0.5)
      .slice(0, 4)
      .map((o, i) => ({
        ...o,
        label: LETTERS[i],
        latex: intervalLatex(o.boundary, o.dir, o.closed),
      }));

    const correctIndex = options.findIndex((o) => o.isCorrect);
    if (correctIndex < 0) continue;

    return {
      type: "MaturaLinearInequalityGraphClosedTask",
      data: {
        variant,
        m,
        n,
        s,
        p,
        lhsLatex,
        rhsLatex,
        rhsStyle,
        inequalityLatex,
        rel,
        finalRel,
        lhsCoeff,
        lhsConst: lhsConstTarget,
        rhsCoeff,
        rhsConst,
        K,
        z,
        closed,
        dir,
        options,
        correctIndex,
      },
    };
  }
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Dana jest nierówność <InlineMath math={data.inequalityLatex} />.
      </p>
      <p>
        Na którym rysunku poprawnie zaznaczono na osi liczbowej zbiór wszystkich
        liczb rzeczywistych spełniających powyższą nierówność?
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt) => (
          <div
            key={opt.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div style={{ width: 26, fontWeight: 700 }}>
              <InlineMath math={`${opt.label}.`} />
            </div>
            <div style={{ flex: 1, maxWidth: 520 }}>
              <NumberLineInterval
                t={opt.boundary}
                tLabelLatex={`${opt.boundary}`}
                dir={opt.dir}
                closed={opt.closed}
                margin={2.8}
                showZero={true}
                width={500}
                height={98}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = LETTERS.map((L, idx) => ({
    value: idx,
    label: L,
    textNode: L,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  if (userAnswer.latex) {
    return <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />;
  }
  return <InlineMath math={`${userAnswer.label}.`} />;
}

export function renderExplanation({ data }) {
  const {
    variant,
    m,
    n,
    s,
    p,
    lhsLatex,
    rhsLatex,
    rhsStyle,
    inequalityLatex,
    lhsCoeff,
    lhsConst,
    rhsCoeff,
    rhsConst,
    K,
    z,
    rel,
    finalRel,
    correctIndex,
    options,
    dir,
    closed,
  } = data;

  const relLatex = relLatexMap[rel];
  const finalRelLatex = relLatexMap[finalRel];
  const cTerm = rhsConst - lhsConst;
  const lhsExpandedLatex =
    variant === "minusParen"
      ? `${p} - ${m * n} + ${coeffX(m * s)}`
      : `${p} + ${m * n} - ${coeffX(m * s)}`;
  const rhsGivenLatex =
    rhsStyle === "constMinusX"
      ? `${rhsConst} - ${coeffX(Math.abs(rhsCoeff))}`
      : linearLatex(rhsCoeff, rhsConst);
  const rhsStdLatex = linearLatex(rhsCoeff, rhsConst);
  const lhsStdLatex = linearLatex(lhsCoeff, lhsConst);
  const reducedLeftLatex = coeffX(K);
  const signFlipInfo =
    K < 0
      ? "Dzielimy przez liczbę ujemną, więc odwracamy znak nierówności."
      : "Dzielimy przez liczbę dodatnią, znak nierówności pozostaje bez zmian.";

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Rozwijamy nawias, wykonujemy mnożenia i porządkujemy:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath
          math={`\\begin{aligned}
${lhsLatex} \\; &${relLatex}\\; ${rhsLatex}\\\\
${lhsExpandedLatex} \\; &${relLatex}\\; ${rhsGivenLatex}\\\\
${lhsStdLatex} \\; &${relLatex}\\; ${rhsStdLatex}\\\\
${reducedLeftLatex} \\; &${relLatex}\\; ${cTerm}
\\end{aligned}`}
        />
      </div>

      <p>
        <strong>Krok 2.</strong> Dzielimy obie strony przez <InlineMath math={`${K}`} />.
        {` ${signFlipInfo}`}
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x\\; ${finalRelLatex} \\; ${z}`} />
      </div>

      <p>
        <strong>Krok 3.</strong> Zapis przedziału:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x\\in ${intervalLatex(z, dir, closed)}`} />
      </div>

      <p>
        Poprawna odpowiedź:{" "}
        <InlineMath
          math={`${options[correctIndex].label}.\\; ${options[correctIndex].latex}`}
        />
      </p>

      <p>
        <strong>Wykres poprawnej odpowiedzi:</strong>
      </p>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <NumberLineInterval
          t={z}
          tLabelLatex={`${z}`}
          dir={dir}
          closed={closed}
          margin={2.8}
          showZero={true}
          width={520}
          height={108}
        />
      </div>
    </div>
  );
}
